import { useMemo } from 'react';
import { map, shareReplay, startWith } from 'rxjs/operators';
import { useObservableValue } from '../../hooks/useObservable';
import { wsScanToDoubleMap, wsScanToMap } from '../../pipes';
import { SecurityTypesUtils, type SecurityTypes } from '../../types';
import type { Security } from '../../types/Security';
import { getOptionCoin } from '../../utils';
import { EMPTY_ARRAY } from '../../utils/empty';
import { shouldDeleteSecurityFromIndex, type SecurityMemoryObservable } from './utils';

export function useOptionIndexes(securityMemoryObs: SecurityMemoryObservable) {
  const optionsObs = useMemo(
    () =>
      securityMemoryObs.pipe(
        map(({ latestMessage }) => latestMessage),
        wsScanToMap<Security, string, SecurityTypes['Option'], SecurityTypes['Option']>({
          getUniqueKey: security => security.Symbol,
          getInsertable: security => security,
          newMapEachUpdate: false,
          isRelevant: SecurityTypesUtils.Option.isType,
          shouldDelete: shouldDeleteSecurityFromIndex,
        }),
        map(optionsBySymbol => [...optionsBySymbol.values()])
      ),
    [securityMemoryObs]
  );

  const optionSecurityBySymbolObs = useMemo(
    () =>
      securityMemoryObs.pipe(
        map(({ latestMessage }) => latestMessage),
        wsScanToMap<Security, string, SecurityTypes['Option'], SecurityTypes['Option']>({
          getUniqueKey: security => security.Symbol,
          getInsertable: security => security,
          newMapEachUpdate: true,
          isRelevant: SecurityTypesUtils.Option.isType,
          shouldDelete: shouldDeleteSecurityFromIndex,
        }),
        startWith(new Map<string, SecurityTypes['Option']>()),
        shareReplay({ bufferSize: 1, refCount: false })
      ),
    [securityMemoryObs]
  );

  const strikesByOptionKeyObs = useMemo(
    () =>
      securityMemoryObs.pipe(
        map(({ latestMessage }) => latestMessage),
        wsScanToDoubleMap<Security, string, string, string, SecurityTypes['Option']>({
          getKey1: getOptionIndexKey,
          getKey2: security => security.Symbol,
          getInsertable: security => security.StrikePrice,
          newOuterMapEachUpdate: true,
          newInnerMapsEachUpdate: true,
          isRelevant: SecurityTypesUtils.Option.isType,
          shouldDelete: shouldDeleteSecurityFromIndex,
          deleteInnerMapOnEmptied: true,
        })
      ),
    [securityMemoryObs]
  );

  const expirationByMarketByCurrencyIdentityObs = useMemo(
    () => optionsObs.pipe(map(getExpirationByMarketByCurrencyIdentity)),
    [optionsObs]
  );
  const options = useObservableValue(() => optionsObs, [optionsObs], EMPTY_ARRAY);
  const optionSecurityBySymbol = useObservableValue(
    () => optionSecurityBySymbolObs,
    [optionSecurityBySymbolObs],
    new Map()
  );
  const strikesByOptionKey = useObservableValue(() => strikesByOptionKeyObs, [strikesByOptionKeyObs], new Map());
  const expirationByMarketByCurrencyIdentity = useObservableValue(
    () => expirationByMarketByCurrencyIdentityObs,
    [expirationByMarketByCurrencyIdentityObs],
    new Map()
  );

  return useMemo(
    () => ({
      options,
      expirationByMarketByCurrencyIdentity,
      optionSecurityBySymbol,
      optionSecurityBySymbolObs,
      strikesByOptionKey,
    }),
    [
      expirationByMarketByCurrencyIdentity,
      optionSecurityBySymbol,
      optionSecurityBySymbolObs,
      options,
      strikesByOptionKey,
    ]
  );
}

function optionKey(option: SecurityTypes['Option']) {
  const { Markets = EMPTY_ARRAY, Expiration } = option;
  return `${getOptionCoin(option)}/${Markets}/${Expiration}`;
}

function getOptionIndexKey(security: Security) {
  return SecurityTypesUtils.Option.isType(security) ? optionKey(security) : '';
}

// Maybe bad naming, assuming Key = Coin / Exchange / Expiry identifies, where Strike + Type are just parameters on that option
export function getStrikesByOptionKey(options: SecurityTypes['Option'][]) {
  const strikesByOptionKey = new Map<string, Map<string, string>>();
  for (const option of options) {
    const { Markets = EMPTY_ARRAY, Expiration, StrikePrice } = option;
    const optionCoin = getOptionCoin(option);
    const key = `${optionCoin}/${Markets}/${Expiration}`;
    const strikesBySymbol = strikesByOptionKey.get(key);

    if (strikesBySymbol) {
      strikesBySymbol.set(option.Symbol, StrikePrice);
    } else {
      strikesByOptionKey.set(key, new Map([[option.Symbol, StrikePrice]]));
    }
  }

  return strikesByOptionKey;
}

export function getExpirationByMarketByCurrencyIdentity(options: SecurityTypes['Option'][]) {
  const expirationByMarketByCurrencyIdentity = new Map<string, Map<string, Set<string>>>();

  for (const option of options) {
    const { Expiration, Markets = EMPTY_ARRAY } = option;
    const key = getOptionCoin(option);

    if (!expirationByMarketByCurrencyIdentity.has(key)) {
      expirationByMarketByCurrencyIdentity.set(key, new Map());
    }
    const expirationByMarket = expirationByMarketByCurrencyIdentity.get(key)!;

    for (const market of Markets) {
      const expirationSet = expirationByMarket.get(market);

      if (expirationSet) {
        expirationSet.add(Expiration);
      } else {
        expirationByMarket.set(market, new Set<string>([Expiration]));
      }
    }
  }

  return expirationByMarketByCurrencyIdentity;
}
